<template>
  <template v-if="!loading">
    <WdsStack direction-column gap-md>
      <div><!--Helps apply the md gap --></div>
      <WdsDivider />

      <WdsStack direction-column gap-sm>
        <WdsStack align-center justify-space-between class="tw-flex-wrap lg:tw-flex-nowrap tw-px-2">
          <WdsText class="compliance-actor-title-style" :text="$t('YOUR ACTIONS')" />
          <WdsText
            v-if="displayedCompliances.length > 0"
            :text="$t('You need to complete the following tasks')"
            small
            secondary
          />
          <WdsText v-else :text="$t('No outstanding tasks')" small secondary />
        </WdsStack>
        <WdsStack
          v-if="displayedCompliances.length > 0 || $slots['worker-actions'] || classificationAnswers"
          direction-column
          gap-xs
          class="tw-px-2"
        >
          <slot name="worker-actions"></slot>
          <div
            v-for="compliance in displayedCompliances"
            :key="compliance.name"
            class="worker-actions"
          >
            <HireComplianceBackgroundChecksWorkerAlert
              v-if="compliance.name === workerRelevantComplianceName"
              :compliance="compliance"
              :hire-id="hireId"
            />
            <HireComplianceAlert
              v-else
              :compliance="compliance"
              :hire-id="hireId"
              :data-test="`compliance-status-${compliance.name}`"
            />
          </div>
          <HireAlert
            v-if="classificationAnswers"
            :title="classificationAnswers.title"
            :description="classificationAnswers.description"
            :href="classificationAnswers.action"
            :button-text="classificationAnswers.buttonText ?? undefined"
            :icon="WdsIconNameEnum.DOCUMENT"
            status="none"
            data-test="worker-classification-answers"
          />
        </WdsStack>
      </WdsStack>

      <template v-if="clientCompliances.length > 0">
        <WdsDivider />

        <WdsStack direction-column gap-sm>
          <WdsStack align-center justify-space-between class="tw-flex-wrap lg:tw-flex-nowrap tw-px-2">
            <WdsText small class="compliance-actor-title-style" :text="$t('CLIENT ACTIONS')" />
            <WdsText
              v-if="overlappingPayeCompliances.length > 0"
              :text="$t('Track outstanding tasks for the client.')"
              small
              secondary
            />
          </WdsStack>
          <WdsStack direction-column gap-xs class="tw-px-2">
            <HireComplianceOverlappingPayeWorkerAlert
              v-for="compliance in overlappingPayeCompliances"
              :key="compliance.name"
              :compliance="compliance"
              :hire-id="hireId"
            />
            <!-- The client's own tasks are theirs to track; the worker only needs to know there are some. -->
            <WdsStack
              v-if="otherClientCompliances.length > 0"
              class="client-actions"
              align-center
              justify-space-between
              data-test="client-actions-summary"
            >
              <WdsText
                :text="$t('The client has some outstanding actions to complete before the hire can progress.')"
              />
            </WdsStack>
          </WdsStack>
        </WdsStack>
      </template>
    </WdsStack>
  </template>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { WdsDivider, WdsIconNameEnum, WdsStack, WdsText } from '@worksome/wds-components'
import HireAlert from '@/components/Hire/HireAlert.vue'
import HireComplianceAlert from '@/components/Hire/HirePageCompliance/HireComplianceAlert.vue'
import HireComplianceBackgroundChecksWorkerAlert from '@/components/Hire/HirePageCompliance/HireComplianceBackgroundChecksWorkerAlert.vue'
import HireComplianceOverlappingPayeWorkerAlert from '@/components/Hire/HirePageCompliance/HireComplianceOverlappingPayeWorkerAlert.vue'
import { useFragment } from '@/gql'
import {
  ComplianceActionAlertButtonFragmentDoc,
  ComplianceActorTypes,
  ComplianceName,
} from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

const props = defineProps<{
  compliances: any[]
  loading: boolean
  hireId: string
}>()

const workerRelevantComplianceName = ComplianceName.BackgroundChecks
const overlappingPayeComplianceName = ComplianceName.UkPayrollContractPeriod

// Worker compliances (Worker/Recruiter actor) + worker-relevant from company/Worksome (e.g. BACKGROUND_CHECKS)
const displayedCompliances = computed(() =>
  props.compliances.filter(
    (c) =>
      !c.completed &&
      (c.actor === ComplianceActorTypes.Worker ||
        c.actor === ComplianceActorTypes.Recruiter ||
        (c.actor === ComplianceActorTypes.Worksome && c.name === workerRelevantComplianceName)),
  ),
)

// Once the classification questions are answered, the row still links back to the answers. It is
// not a task, so it sits below the outstanding ones and is not counted.
const classificationAnswers = computed(() => {
  const compliance = props.compliances.find(
    (c) => c.name === ComplianceName.DeterminationHireFreelancerInput && c.completed,
  )

  return compliance?.action
    ? useFragment(ComplianceActionAlertButtonFragmentDoc, compliance.action)
    : null
})

// Pending client (company) actions — read-only visibility so the worker can
// see what's blocking progress on their side (e.g. a classification re-run).
const clientCompliances = computed(() =>
  props.compliances.filter(
    (c) => c.actor === ComplianceActorTypes.Company && !c.completed,
  ),
)
const overlappingPayeCompliances = computed(() =>
  clientCompliances.value.filter((c) => c.name === overlappingPayeComplianceName),
)
const otherClientCompliances = computed(() =>
  clientCompliances.value.filter((c) => c.name !== overlappingPayeComplianceName),
)
</script>

<style scoped>

.compliance-actor-title-style:deep(div) {
  @apply tw-text-gray-800;
  white-space: nowrap;
  font-size: 0.75rem;
  font-weight: 600;
}

.worker-actions {
  background-color: white;
}

.client-actions {
  background-color: white;
  @apply tw-rounded tw-px-3 tw-py-2;
}
</style>
